import React, { useMemo, useRef, useState } from 'react';
import {
  X, Upload, FileSpreadsheet, Download, Loader2, CheckCircle2,
  AlertTriangle, ArrowLeft, Copy
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import Button from '../common/Button/Button';
import paymentImportService from '../../api/crm/orders/paymentImportService';
import { PAYMENT_METHOD_CODES } from '../../constants/paymentMethods';
import { formatIndianAmount } from '../../utils/amountFormatter';

const MAX_ROWS = 2000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const STEP = { UPLOAD: 'upload', PREVIEW: 'preview', IMPORTING: 'importing', RESULT: 'result' };

const RESULT_COLUMNS = [
  'invoiceNumber',
  'invoiceYear',
  'amountReceived',
  'paymentDate',
  'paymentMethod',
  'paymentReference',
  'remarks'
];

const fmtAmount = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const num = Number(value);
  if (!Number.isFinite(num)) return '--';
  return `₹${formatIndianAmount(num)}`;
};

const statusBadge = (status) => {
  switch (status) {
    case 'Valid':
    case 'Success':
      return 'bg-green-100 text-green-700 border border-green-200';
    case 'Warning':
      return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    case 'Error':
    case 'Failed':
      return 'bg-red-100 text-red-700 border border-red-200';
    case 'Skipped':
      return 'bg-gray-100 text-gray-600 border border-gray-200';
    default:
      return 'bg-gray-100 text-gray-600 border border-gray-200';
  }
};

const todayFileStamp = () => {
  const t = new Date();
  return `${String(t.getDate()).padStart(2, '0')}-${String(t.getMonth() + 1).padStart(2, '0')}-${t.getFullYear()}`;
};

const formatProcessingTime = (ms) => {
  if (!ms && ms !== 0) return '--';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} sec`;
};

const BulkPaymentImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(STEP.UPLOAD);
  const [fileName, setFileName] = useState('');
  const [rawRows, setRawRows] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validation, setValidation] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, percent: 0, currentInvoice: null });

  const fileInputRef = useRef(null);
  const importStartedRef = useRef(false);

  const reset = () => {
    setStep(STEP.UPLOAD);
    setFileName('');
    setRawRows([]);
    setIsParsing(false);
    setIsValidating(false);
    setIsImporting(false);
    setValidation(null);
    setDecisions({});
    setImportResult(null);
    setImportProgress({ current: 0, total: 0, percent: 0, currentInvoice: null });
    importStartedRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    // Block closing only while an import is actively running (transaction safety).
    // Once it completes (RESULT step) or before it starts, closing is allowed.
    if (isImporting && step === STEP.IMPORTING) return;
    reset();
    onClose?.();
  };

  const handleDownloadTemplate = async () => {
    const result = await paymentImportService.downloadTemplate();
    if (!result.success) toast.error(result.error || 'Failed to download template');
  };

  const runValidation = async (rows) => {
    setIsValidating(true);
    setStep(STEP.PREVIEW);
    const result = await paymentImportService.validate(rows);
    setIsValidating(false);

    if (!result.success) {
      toast.error(result.error || 'Validation failed');
      setStep(STEP.UPLOAD);
      return;
    }

    setDecisions({});
    setValidation({ summary: result.summary, results: result.results || [] });
  };

  const parseFile = (file) => {
    if (!file) return;
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isCsv = file.name.endsWith('.csv');
    if (!isExcel && !isCsv) {
      toast.error('Please select a CSV or Excel file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size should be less than 10MB.');
      return;
    }

    setIsParsing(true);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!rows.length) {
          toast.error('The file has no data rows.');
          setIsParsing(false);
          return;
        }
        if (rows.length > MAX_ROWS) {
          toast.error(`The file has ${rows.length} rows, which exceeds the maximum of ${MAX_ROWS} rows per import.`);
          setIsParsing(false);
          return;
        }

        setRawRows(rows);
        setIsParsing(false);
        runValidation(rows);
      } catch (err) {
        toast.error('Failed to read the file. Please use the provided template.');
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read the file.');
      setIsParsing(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const setDecision = (row, value) => {
    setDecisions((prev) => ({ ...prev, [row]: value }));
  };

  const duplicateRows = useMemo(
    () => (validation?.results || []).filter((r) => r.status === 'Warning'),
    [validation]
  );

  const resolvedDuplicateCount = useMemo(
    () => duplicateRows.filter((r) => decisions[r.row] !== undefined).length,
    [duplicateRows, decisions]
  );

  const allDuplicatesResolved = duplicateRows.length === 0 || resolvedDuplicateCount === duplicateRows.length;

  const importableCount = useMemo(() => {
    if (!validation) return 0;
    return validation.results.reduce((acc, r) => {
      if (r.status === 'Valid') return acc + 1;
      if (r.status === 'Warning' && decisions[r.row] === 'import') return acc + 1;
      return acc;
    }, 0);
  }, [validation, decisions]);

  const handleRejectAllDuplicates = () => {
    const next = { ...decisions };
    duplicateRows.forEach((r) => {
      next[r.row] = 'reject';
    });
    setDecisions(next);
  };

  const handleImport = async () => {
    if (!rawRows.length || !allDuplicatesResolved) return;

    setIsImporting(true);
    importStartedRef.current = true;
    setStep(STEP.IMPORTING);
    setImportProgress({ current: 0, total: importableCount, percent: 0, currentInvoice: null });

    const result = await paymentImportService.importWithProgress(
      rawRows,
      decisions,
      fileName,
      (progress) => setImportProgress(progress)
    );

    setIsImporting(false);

    if (!result.success) {
      importStartedRef.current = false;
      toast.error(result.error || 'Import failed');
      setStep(STEP.PREVIEW);
      return;
    }

    setImportResult(result);
    setStep(STEP.RESULT);
    toast.success('Bulk payment import completed');
    onSuccess?.();
  };

  const handleDownloadResult = () => {
    if (!importResult?.results?.length) return;
    const rows = importResult.results.map((r) => ({
      invoiceNumber: r.invoiceNumber,
      invoiceYear: r.invoiceYear,
      amountReceived: r.amountReceived,
      paymentDate: r.paymentDate,
      paymentMethod: r.paymentMethod,
      paymentReference: r.paymentReference,
      remarks: r.remarks,
      Status: r.status,
      Reason: r.reason || '',
      'Payment Number': r.paymentNumber || '',
      'Imported At': r.importedAt || ''
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: [...RESULT_COLUMNS, 'Status', 'Reason', 'Payment Number', 'Imported At']
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Import Result');
    XLSX.writeFile(workbook, `bulk-payment-import-result-${todayFileStamp()}.xlsx`);
  };

  if (!isOpen) return null;

  const summary = validation?.summary;
  const resultSummary = importResult?.summary;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-agrosaf-green/10 rounded-lg">
              <FileSpreadsheet className="h-5 w-5 text-agrosaf-green" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Bulk Payment Import</h3>
              <p className="text-sm text-gray-500">Record payments against sales invoices from an Excel file</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isImporting && step === STEP.IMPORTING}
            className={`p-2 rounded-full ${isImporting && step === STEP.IMPORTING ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            title={isImporting && step === STEP.IMPORTING ? 'Cannot cancel while import is running' : 'Close'}
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
          {step === STEP.UPLOAD && (
            <div className="max-w-3xl mx-auto space-y-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-xl border border-blue-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-50 rounded-lg"><Download className="h-5 w-5 text-blue-600" /></div>
                    <span className="font-semibold text-gray-900">1. Download Template</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Use the template columns exactly. Invoice type is always treated as Sales.
                  </p>
                  <Button variant="outline" size="sm" icon={<Download className="h-4 w-4" />} onClick={handleDownloadTemplate} className="w-full justify-center text-blue-600 border-blue-200 hover:bg-blue-50">
                    Download Template
                  </Button>
                </div>
                <div className="bg-white p-5 rounded-xl border border-green-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-50 rounded-lg"><FileSpreadsheet className="h-5 w-5 text-green-600" /></div>
                    <span className="font-semibold text-gray-900">2. Prepare File</span>
                  </div>
                  <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4">
                    <li>invoiceNumber, invoiceYear, amountReceived, paymentDate, paymentMethod are required.</li>
                    <li>paymentReference and remarks are optional.</li>
                    <li>Dates: YYYY-MM-DD or DD/MM/YYYY.</li>
                    <li>Maximum {MAX_ROWS} rows per file.</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-gray-900 text-sm">Accepted Payment Methods</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(PAYMENT_METHOD_CODES.join(', '));
                      toast.success('Payment methods copied');
                    }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-agrosaf-green hover:underline"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy all
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Use these exact values in the <code className="px-1 bg-gray-100 rounded">paymentMethod</code> column. Click any value to copy it.
                </p>
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_METHOD_CODES.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(code);
                        toast.success(`Copied "${code}"`);
                      }}
                      title="Click to copy"
                      className="px-2.5 py-1 text-xs font-mono font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-md hover:bg-agrosaf-green/10 hover:border-agrosaf-green hover:text-agrosaf-green transition-colors"
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>

              <div
                className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all ${isParsing ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 hover:border-agrosaf-green hover:bg-green-50/20'}`}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  parseFile(e.dataTransfer.files[0]);
                }}
              >
                <input type="file" ref={fileInputRef} onChange={(e) => parseFile(e.target.files[0])} accept=".csv,.xlsx,.xls" className="hidden" />
                {isParsing ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                    <p className="text-lg font-medium text-gray-900">Reading file...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-4">
                      <Upload className="h-8 w-8 text-agrosaf-green" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Click to select or drag & drop</h4>
                    <p className="text-sm text-gray-500 mb-6">CSV or Excel, up to 10MB</p>
                    <Button variant="primary" onClick={() => fileInputRef.current?.click()} className="bg-agrosaf-green hover:bg-agrosaf-green-hover px-8">
                      Select File
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === STEP.PREVIEW && (
            <div className="space-y-5">
              {isValidating ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="h-10 w-10 text-agrosaf-green animate-spin mb-4" />
                  <p className="text-gray-700 font-medium">Validating {rawRows.length} rows...</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <SummaryCard label="Total Rows" value={summary?.totalRows || 0} tone="gray" />
                    <SummaryCard label="Valid Rows" value={summary?.valid || 0} tone="green" />
                    <SummaryCard label="Warning Rows" value={summary?.warning || 0} tone="yellow" />
                    <SummaryCard label="Error Rows" value={summary?.error || 0} tone="red" />
                  </div>

                  {duplicateRows.length > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>{resolvedDuplicateCount} of {duplicateRows.length}</strong> duplicate row(s) resolved.
                          Choose <strong>Reject</strong> or <strong>Import Anyway</strong> for each duplicate before importing.
                        </span>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleRejectAllDuplicates} className="shrink-0">
                        Reject all duplicates
                      </Button>
                    </div>
                  )}

                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">Row Details</span>
                      <span className="text-xs text-gray-500">{importableCount} row(s) will be imported</span>
                    </div>
                    <div className="max-h-[45vh] overflow-auto">
                      <table className="w-full text-left border-collapse min-w-[1100px]">
                        <thead className="sticky top-0 bg-gray-50 z-10">
                          <tr>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase">Row</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase">Invoice</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase text-right">Invoice Total</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase text-right">Already Paid</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase text-right">Outstanding</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase text-right">Payment</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase text-right">Remaining</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase">Status</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase">Reason</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-gray-500 uppercase">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {validation?.results.map((r) => (
                            <tr key={r.row} className="hover:bg-gray-50/50">
                              <td className="px-2 py-2 text-xs text-gray-600">{r.row}</td>
                              <td className="px-2 py-2 text-xs text-gray-900 font-medium whitespace-nowrap">
                                {r.invoiceNumber || '--'}{r.invoiceYear ? ` / ${r.invoiceYear}` : ''}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-700 text-right whitespace-nowrap">{fmtAmount(r.invoiceTotal)}</td>
                              <td className="px-2 py-2 text-xs text-gray-700 text-right whitespace-nowrap">{fmtAmount(r.alreadyPaid)}</td>
                              <td className="px-2 py-2 text-xs text-orange-700 font-medium text-right whitespace-nowrap">{fmtAmount(r.outstanding)}</td>
                              <td className="px-2 py-2 text-xs text-green-700 font-medium text-right whitespace-nowrap">{fmtAmount(r.paymentAmount ?? r.amountReceived)}</td>
                              <td className="px-2 py-2 text-xs text-gray-700 text-right whitespace-nowrap">{fmtAmount(r.remainingAfterImport)}</td>
                              <td className="px-2 py-2">
                                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusBadge(r.status)}`}>
                                  {r.status}
                                </span>
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-600 max-w-[200px]">{r.reason || '--'}</td>
                              <td className="px-2 py-2">
                                {r.status === 'Warning' ? (
                                  <div className="flex flex-col gap-1 text-xs">
                                    <label className="inline-flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`dup-${r.row}`}
                                        checked={decisions[r.row] === 'reject'}
                                        onChange={() => setDecision(r.row, 'reject')}
                                      />
                                      <span>Reject</span>
                                    </label>
                                    <label className="inline-flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`dup-${r.row}`}
                                        checked={decisions[r.row] === 'import'}
                                        onChange={() => setDecision(r.row, 'import')}
                                      />
                                      <span>Import Anyway</span>
                                    </label>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-300">--</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {step === STEP.IMPORTING && (
            <div className="max-w-lg mx-auto py-16 text-center space-y-6">
              <Loader2 className="h-12 w-12 text-agrosaf-green animate-spin mx-auto" />
              <div>
                <h4 className="text-xl font-bold text-gray-900">Importing Payments...</h4>
                <p className="text-sm text-gray-500 mt-1">Please do not close this window.</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{importProgress.current} / {importProgress.total || importableCount}</span>
                  <span>{importProgress.percent}%</span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-agrosaf-green transition-all duration-300 rounded-full"
                    style={{ width: `${importProgress.percent}%` }}
                  />
                </div>
                {importProgress.currentInvoice && (
                  <p className="text-xs text-gray-500">Processing invoice: <strong>{importProgress.currentInvoice}</strong></p>
                )}
              </div>
            </div>
          )}

          {step === STEP.RESULT && importResult && (
            <div className="max-w-4xl mx-auto py-6 text-center space-y-6">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-9 w-9 text-green-600" />
              </div>
              <div>
                <h4 className="text-2xl font-bold text-gray-900">Import Completed</h4>
                <p className="text-sm text-gray-500 mt-1">Batch {importResult.batchId}</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <SummaryCard label="Total Uploaded" value={resultSummary?.totalUploaded || 0} tone="gray" />
                <SummaryCard label="Imported" value={resultSummary?.imported || 0} tone="green" />
                <SummaryCard label="Skipped" value={resultSummary?.skipped || 0} tone="yellow" />
                <SummaryCard label="Warnings" value={resultSummary?.warnings || 0} tone="yellow" />
                <SummaryCard label="Errors" value={resultSummary?.errors || 0} tone="red" />
                <SummaryCard label="Failed" value={resultSummary?.failed || 0} tone="red" />
              </div>
              <p className="text-sm text-gray-600">
                Processing time: <strong>{formatProcessingTime(resultSummary?.processingTimeMs)}</strong>
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" icon={<Download className="h-4 w-4" />} onClick={handleDownloadResult}>
                  Download Result
                </Button>
                <Button variant="primary" onClick={handleClose} className="bg-agrosaf-green hover:bg-agrosaf-green-hover">
                  Done
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Download the result file to review failed or skipped rows, fix them, and upload again.
              </p>
            </div>
          )}
        </div>

        {step === STEP.PREVIEW && !isValidating && (
          <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
            <Button variant="outline" icon={<ArrowLeft className="h-4 w-4" />} onClick={reset} disabled={isImporting}>
              Upload Another
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={isImporting || importableCount === 0 || !allDuplicatesResolved}
              className="bg-agrosaf-green hover:bg-agrosaf-green-hover px-10"
              title={
                !allDuplicatesResolved
                  ? `Resolve all ${duplicateRows.length} duplicate row(s) before importing`
                  : undefined
              }
            >
              {`Import ${importableCount} Payment${importableCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const toneMap = {
  gray: 'text-gray-900',
  green: 'text-green-600',
  yellow: 'text-yellow-500',
  red: 'text-red-500'
};

const SummaryCard = ({ label, value, tone = 'gray' }) => (
  <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
    <div className={`text-2xl font-bold ${toneMap[tone]}`}>{value}</div>
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-1">{label}</div>
  </div>
);

export default BulkPaymentImportModal;
